/**
 * AcuityServiceMatchModal - side-by-side matching of Phygital services with Acuity services.
 * RULE: Phygital is source of truth. Sync = match, never override.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Link2, Plus, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcuityServiceMatchModal({ open, onClose, eventId }) {
  const [phygical, setPhygical] = useState([]);
  const [acuity, setAcuity] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // mappings: { [acuity_id]: { action: 'match'|'import'|'skip', phygital_service_id?: string } }
  const [mappings, setMappings] = useState({});

  const auth = useCallback(() => {
    const t = localStorage.getItem("token") || localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const loadPreview = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/acuity/sync-services-preview`, { event_id: eventId },
        { headers: auth(), withCredentials: true });
      setPhygical(res.data?.phygital_services || []);
      setAcuity(res.data?.acuity_services || []);
      setConnected(res.data?.acuity_connected || false);
      // Default action for each acuity service: match (try to auto-pick by name)
      const initial = {};
      (res.data?.acuity_services || []).forEach((a) => {
        const exact = (res.data?.phygital_services || []).find(
          (p) => p.name?.toLowerCase().trim() === a.name?.toLowerCase().trim()
        );
        initial[a.id] = exact
          ? { action: "match", phygital_service_id: exact.service_id }
          : { action: "import" };
      });
      setMappings(initial);
    } catch (e) {
      toast.error("Could not load Acuity sync data");
    } finally {
      setLoading(false);
    }
  }, [eventId, auth]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  const updateMapping = (acuityId, patch) => {
    setMappings((prev) => ({ ...prev, [acuityId]: { ...(prev[acuityId] || {}), ...patch } }));
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const m = acuity.map((a) => ({
        action: mappings[a.id]?.action || "skip",
        acuity_service: a,
        phygital_service_id: mappings[a.id]?.phygital_service_id,
      }));
      const res = await axios.post(`${API}/acuity/sync-services-confirm`,
        { event_id: eventId, mappings: m },
        { headers: auth(), withCredentials: true });
      toast.success(`${res.data.matched} matched, ${res.data.imported} imported, ${res.data.skipped} skipped`);
      onClose();
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Match your Acuity services with Phygital</DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            Phygital stays as your source of truth. For each Acuity service, choose to match it to an existing Phygital service, import it as new, or skip it. Phygital data (descriptions, looks, products) is never overwritten.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          {!connected ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Acuity is not connected</p>
                  <p className="text-xs text-gray-600 mt-1">Connect your Acuity account in Settings → Tech Stack to pull your services.</p>
                </div>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : acuity.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No services found in your Acuity account.</p>
          ) : (
            <div className="space-y-3">
              {acuity.map((a) => {
                const map = mappings[a.id] || {};
                return (
                  <Card key={a.id} className="border-gray-200" data-testid={`acuity-service-row-${a.id}`}>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-12 gap-3 items-start">
                        {/* Acuity service (left) */}
                        <div className="col-span-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[9px] bg-purple-50 border-purple-200 text-purple-700">ACUITY</Badge>
                            <p className="font-medium text-sm">{a.name}</p>
                          </div>
                          <p className="text-[11px] text-gray-500 line-clamp-2">{a.description}</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {a.duration} min • ${a.price || 0}
                          </p>
                        </div>

                        {/* Action selector (middle) */}
                        <div className="col-span-3 space-y-1.5">
                          <Select value={map.action} onValueChange={(v) => updateMapping(a.id, { action: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`action-${a.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="match"><Link2 className="w-3 h-3 inline mr-1" /> Match existing</SelectItem>
                              <SelectItem value="import"><Plus className="w-3 h-3 inline mr-1" /> Import as new</SelectItem>
                              <SelectItem value="skip"><X className="w-3 h-3 inline mr-1" /> Skip</SelectItem>
                            </SelectContent>
                          </Select>
                          {map.action === "match" && (
                            <Select
                              value={map.phygital_service_id}
                              onValueChange={(v) => updateMapping(a.id, { phygital_service_id: v })}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`pick-phygital-${a.id}`}>
                                <SelectValue placeholder="Pick Phygital service" />
                              </SelectTrigger>
                              <SelectContent>
                                {phygical.map((p) => (
                                  <SelectItem key={p.service_id} value={p.service_id}>{p.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        {/* Phygital preview (right) */}
                        <div className="col-span-4">
                          {map.action === "match" && map.phygital_service_id && (() => {
                            const p = phygical.find((s) => s.service_id === map.phygital_service_id);
                            if (!p) return null;
                            return (
                              <div className="bg-green-50 border border-green-200 rounded-md p-2">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <Badge variant="outline" className="text-[9px] bg-green-50 border-green-300 text-green-700">PHYGITAL</Badge>
                                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                                </div>
                                <p className="text-xs font-medium">{p.name}</p>
                                <p className="text-[10px] text-gray-500">${p.price} • {p.duration_minutes} min</p>
                              </div>
                            );
                          })()}
                          {map.action === "import" && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                              <p className="text-[10px] text-blue-700">Will create a new Phygital service from this Acuity entry.</p>
                            </div>
                          )}
                          {map.action === "skip" && (
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                              <p className="text-[10px] text-gray-500">Will not be linked to Phygital.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={!connected || saving} data-testid="confirm-acuity-sync">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Confirm matches
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
